import algoliasearch from "algoliasearch";
import { ALGOLIA_INDEX } from "../../config/firebaseVariant";

// Initialize Algolia client
const client = algoliasearch("VNZT5K7SCI", "a802cd95fb94fb3b6c2916b1161b93c8");
const index = client.initIndex(ALGOLIA_INDEX);

export const getPublicSVGsWithAlgolia = async (page = 0, hitsPerPage = 24) => {
  try {
    const result = await index.search("", {
      hitsPerPage,
      page,
      filters: "isPublic:true",
      aroundLatLngViaIP: false,
      facets: ["*"],
    });

    // Convert hits to your desired format
    const svgFiles = result.hits.map((hit) => ({
      id: hit.objectID,
      ...hit,
    }));

    return {
      files: svgFiles,
      totalPages: result.nbPages,
      currentPage: result.page,
      hasMore: result.page < result.nbPages - 1,
      totalHits: result.nbHits,
    };
  } catch (error) {
    console.error("Failed to fetch public SVGs:", error);
    throw error;
  }
};

export const searchPublicSVGsWithAlgolia = async (query = "", page = 0, hitsPerPage = 24) => {
  try {
    console.log("🔍 Algolia Search starting:", { query, page, hitsPerPage });
    
    const searchParams = {
      hitsPerPage,
      page,
      filters: "isPublic:true",
      aroundLatLngViaIP: false,
      facets: ["*"],
    };
    
    console.log("📋 Search parameters:", searchParams);
    
    const result = await index.search(query, searchParams);

    console.log("📊 Algolia Response received:", {
      nbHits: result.nbHits,
      nbPages: result.nbPages,
      query: result.query,
      processingTimeMS: result.processingTimeMS,
      hitsLength: result.hits?.length
    });

    // Convert hits to your desired format
    const svgFiles = result.hits.map((hit) => ({
      id: hit.objectID,
      ...hit,
    }));

    const returnData = {
      files: svgFiles,
      totalPages: result.nbPages,
      currentPage: result.page,
      hasMore: result.page < result.nbPages - 1,
      totalHits: result.nbHits,
      query,
    };
    
    console.log("✅ Search completed, returning:", {
      filesCount: returnData.files.length,
      hasMore: returnData.hasMore,
      totalHits: returnData.totalHits
    });

    return returnData;
  } catch (error) {
    console.error("❌ Failed to search public SVGs:", error);
    console.error("Error details:", error);
    throw error;
  }
};

// Function to configure index settings to make isPublic filterable and enable search
export const configureAlgoliaIndexForFiltering = async () => {
  try {
    const settings = {
      attributesForFaceting: [
        'filterOnly(isPublic)',
        'filterOnly(ownerUid)',
        'searchable(searchKeywords)',  // Enable filtering by keywords
        'searchable(category)'          // Enable filtering by category
      ],
      searchableAttributes: [
        'seoTitle',           // Highest priority - the main title
        'searchKeywords',     // Keywords we generated with Gemini  
        'seoDescription',     // Description
        'prompt',             // Original prompt (fallback)
        'category'            // Category if available
      ],
      attributesToRetrieve: [
        'prompt',
        'seoTitle',
        'seoDescription', 
        'searchKeywords',
        'category',
        'filePath',
        'isPublic',
        'ownerUid',
        'createdAt',
        'downloadCount',
        'ownerDisplayName',
        'workflow',
        'stitchStats'      // Stitch count / colors shown on EmbroideryTrace cards
      ],
      // Ranking configuration for better results
      ranking: [
        'typo',
        'geo',
        'words',
        'filters',
        'proximity',
        'attribute',
        'exact',
        'custom'
      ]
    };
    
    console.log("🔧 Configuring Algolia index with settings:", settings);
    const response = await index.setSettings(settings);
    await index.waitTask(response.taskID);
    
    console.log("✅ Algolia index configured successfully");
    return response;
  } catch (error) {
    console.error("Failed to configure index settings:", error);
    throw error;
  }
};

// Function to search for artwork by a specific creator
export const searchPublicSVGsByCreator = async (ownerUid, page = 0, hitsPerPage = 24) => {
  try {
    console.log("👨‍🎨 Algolia Creator Search starting:", { ownerUid, page, hitsPerPage });
    
    const searchParams = {
      hitsPerPage,
      page,
      filters: `isPublic:true AND ownerUid:"${ownerUid}"`,
      aroundLatLngViaIP: false,
      facets: ["*"],
    };
    
    console.log("📋 Creator search parameters:", searchParams);
    
    const result = await index.search("", searchParams);

    console.log("📊 Creator Search Response:", {
      nbHits: result.nbHits,
      nbPages: result.nbPages,
      processingTimeMS: result.processingTimeMS,
      hitsLength: result.hits?.length
    });

    // Convert hits to your desired format
    const svgFiles = result.hits.map((hit) => ({
      id: hit.objectID,
      ...hit,
    }));

    const returnData = {
      files: svgFiles,
      totalPages: result.nbPages,
      currentPage: result.page,
      hasMore: result.page < result.nbPages - 1,
      totalHits: result.nbHits,
      creatorUid: ownerUid,
    };
    
    console.log("✅ Creator search completed:", {
      filesCount: returnData.files.length,
      hasMore: returnData.hasMore,
      totalHits: returnData.totalHits
    });

    return returnData;
  } catch (error) {
    console.error("❌ Failed to search creator's SVGs:", error);
    console.error("Error details:", error);
    throw error;
  }
};

// Function to test search functionality and debug
export const testAlgoliaSearch = async (query = "disney") => {
  try {
    console.log("🧪 Testing Algolia search with query:", query);
    
    // Test basic search
    const result = await index.search(query, {
      hitsPerPage: 5,
      page: 0,
      filters: "isPublic:true"
    });
    
    console.log("📊 Test Results:", {
      query: result.query,
      nbHits: result.nbHits,
      processingTimeMS: result.processingTimeMS,
      hits: result.hits.map(hit => ({
        objectID: hit.objectID,
        prompt: hit.prompt,
        seoTitle: hit.seoTitle,
        seoDescription: hit.seoDescription,
        searchKeywords: hit.searchKeywords
      }))
    });
    
    return result;
  } catch (error) {
    console.error("❌ Test search failed:", error);
    throw error;
  }
};

// Function to get popular keywords/tags for category browsing
export const getPopularKeywords = async (limit = 20) => {
  try {
    console.log("🏷️ Fetching popular keywords...");
    
    const result = await index.search("", {
      hitsPerPage: 0,  // We only want facets, not hits
      filters: "isPublic:true",
      facets: ["searchKeywords"],
      maxValuesPerFacet: limit
    });
    
    // Extract and sort keywords by count
    const keywordFacets = result.facets?.searchKeywords || {};
    const sortedKeywords = Object.entries(keywordFacets)
      .map(([keyword, count]) => ({ keyword, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, limit);
    
    console.log("✅ Popular keywords:", sortedKeywords);
    return sortedKeywords;
  } catch (error) {
    console.error("❌ Failed to get popular keywords:", error);
    throw error;
  }
};

// Function to search with keyword filter (for category browsing)
export const searchByKeyword = async (keyword, page = 0, hitsPerPage = 24) => {
  try {
    console.log("🏷️ Searching by keyword:", keyword);
    
    const result = await index.search("", {
      hitsPerPage,
      page,
      filters: `isPublic:true AND searchKeywords:"${keyword}"`,
      facets: ["searchKeywords"],
    });
    
    const svgFiles = result.hits.map((hit) => ({
      id: hit.objectID,
      ...hit,
    }));
    
    return {
      files: svgFiles,
      totalPages: result.nbPages,
      currentPage: result.page,
      hasMore: result.page < result.nbPages - 1,
      totalHits: result.nbHits,
      keyword,
    };
  } catch (error) {
    console.error("❌ Failed to search by keyword:", error);
    throw error;
  }
};

// Function to get search suggestions (autocomplete)
export const getSearchSuggestions = async (query, limit = 8) => {
  try {
    if (!query || query.length < 2) return [];
    
    const result = await index.search(query, {
      hitsPerPage: limit,
      filters: "isPublic:true",
      attributesToRetrieve: ["seoTitle", "searchKeywords"],
      attributesToHighlight: ["seoTitle"],
    });
    
    // Extract unique titles and keywords as suggestions
    const suggestions = new Set();
    
    result.hits.forEach(hit => {
      if (hit.seoTitle) {
        suggestions.add(hit.seoTitle);
      }
      if (hit.searchKeywords) {
        hit.searchKeywords.forEach(kw => {
          if (kw.toLowerCase().includes(query.toLowerCase())) {
            suggestions.add(kw);
          }
        });
      }
    });
    
    return Array.from(suggestions).slice(0, limit);
  } catch (error) {
    console.error("❌ Failed to get suggestions:", error);
    return [];
  }
};
